import { useCallback, useEffect, useState } from 'react';
import { OptionListOptions } from '../../hooks/makeOptionsList';
import { restApi } from '../../services/api';
import { TeceoApiResponse } from '../../types/request.types';
import { useAuthContext } from '../login/AuthContext';
import { Segment, SegmentOption } from './segment.type';

type SegmentsState = {
  items: Segment[];
  options: SegmentOption[];
  loading: boolean;
};

type SegmentsActions = {
  load: () => Promise<Segment[]>;
  getSelectedItems: (selectedIds: string[]) => Promise<Segment[]>;
};

export type SegmentsTranslation = {
  bijuterias: string;
  feminino: string;
  masculino: string;
  infantil: string;
  bebê: string;
  jeans: string;
  casual: string;
  festa: string;
  fitness: string;
  praia: string;
  lingerie: string;
  pijamas: string;
  vestuário: string;
  calçados: string;
  bolsas: string;
  joias: string;
  semijoias: string;
  óculos: string;
  decoração: string;
  perfumes: string;
  cosméticos: string;
  'cama, mesa e banho': string;
  'plus size': string;
  'eco-friendly': string;
};

const segmentsTranslation: SegmentsTranslation = {
  bijuterias: 'fashion jewelry',
  feminino: 'womens',
  masculino: 'mens',
  infantil: 'kids',
  bebê: 'baby',
  jeans: 'denim',
  casual: 'ready to wear',
  festa: 'special occasion',
  fitness: 'fitness',
  praia: 'beachwear',
  lingerie: 'lingerie',
  pijamas: 'sleepwear',
  vestuário: 'clothing',
  calçados: 'shoes',
  bolsas: 'handbags',
  joias: 'fine jewelry',
  semijoias: 'semi-fine jewelry',
  óculos: 'eyewear',
  decoração: 'home decor',
  perfumes: 'fragrance',
  cosméticos: 'cosmetics',
  'cama, mesa e banho': 'bed & bath',
  'plus size': 'plus size',
  'eco-friendly': 'eco-friendly',
};

export const translateSegments = (segmentName: keyof SegmentsTranslation, isPortuguese: boolean) =>
  isPortuguese ? segmentName : segmentsTranslation[segmentName];

const useSegments = ({ autoLoad }: OptionListOptions): [SegmentsState, SegmentsActions] => {
  const { loggedin } = useAuthContext();
  const [items, setItems] = useState<Segment[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const load = useCallback(async () => {
    setLoading(true);
    const response: TeceoApiResponse<Segment[]> = await restApi.get('/segments');

    if (response.ok && response.data) {
      setItems(response.data);
      setLoading(false);
      return response.data;
    }

    setLoading(false);
    return [];
  }, [setItems, setLoading]);

  const getSelectedItems = useCallback(
    async (selectedIds: string[]) => {
      if (!items || items.length === 0) {
        await load();
      }

      const selectedSegments = items.filter((item) => selectedIds.includes(item.id));

      return selectedSegments;
    },
    [items, load],
  );

  useEffect(() => {
    if (autoLoad !== false) {
      if (loggedin) {
        load();
      }
    }
  }, [autoLoad, load, loggedin]);

  return [
    {
      items,
      loading,
      options: items.map((segment) => ({ label: segment.name, value: segment.id })),
    },
    { load, getSelectedItems },
  ];
};

export default useSegments;
